import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { LegacyColumn as Column } from 'components/MaterialTable/MatDataGridTypes';
import { useAuth } from '../../../lib/Auth';
import { ProductListFilter } from './ProductListingFilters';
import { getAllProducts } from '../../../requests/product';
import ProductListingActions from './ProductListingActions';
import styled from 'styled-components';
import { navigate } from 'hooks/useHookRouterCompat';
import { PRODUCT_STATUS, PRODUCT_TYPE } from 'constants/product';
import { getProductTypeConfig } from 'utils/product';
import { renderToast } from 'components/UI/ToastNotification';
import { GENERAL_CONSTANTS } from 'constants/general';
import { useReadPermission } from 'hooks/useUserRoles';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import MatDataGrid from 'components/MaterialTable/MatDataGrid';
import { isProductStatusPublished } from 'utils/product';

const StyledButton = styled.button<any>`
  background: none;
  border: none;
  color: #FC780B;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  margin: 0 5px;
`;

interface Props {
  selectedVendorId?: number;
  filters: ProductListFilter;
  searchTerm: string;
  productColumns: Column<any>[];
  setShowDeleteModal: Function;
  setShowUpdateModal: Function;
  setShowPublishModal: Function;
  setSelectedRows: Function;
  showDeleteModal: boolean;
  showPublishModal: boolean;
  showUpdateModal: 'none' | 'update' | 'revise';
  selectedRows: any[];
  reload: boolean;
  setReload: Function;
  setLoading: Function;
  loading: boolean;
  productType?: string;
}

const ProductListingTable = ({
  selectedVendorId, filters, searchTerm, productColumns, setShowDeleteModal, setShowUpdateModal, showDeleteModal,
  showUpdateModal, setSelectedRows, selectedRows, showPublishModal, setShowPublishModal, reload, setReload, setLoading,
  loading, productType
}: Props) => {
  const { token } = useAuth();
  const [columns, setColumns] = React.useState(productColumns);
  const [selectedProduct, setSelectedProduct] = React.useState<any>();
  const [tablesData, setTablesData] = React.useState<any>({
    data: [],
    page: 0,
    totalCount: 0,
  });
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(0);
  const [sortField, setSortField] = React.useState<string | undefined>();
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc' | undefined>();
  const typeConfig = getProductTypeConfig(productType);
  const { readOnly: readOnlyEditUpdate } = useReadPermission(typeConfig.permissions.editUpdate);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Memoize action column to avoid recreation
  const actionColumn = useMemo(() => {
    const { detailUrl } = typeConfig;
    return {
      render: (rowData: any) => {
        return <FlexboxContainerComponent>
          <div style={{ width: "fit-content" }}>
            <ReadOnlyWrapper readOnly={rowData.status === PRODUCT_STATUS.DRAFT && readOnlyEditUpdate}>
              <StyledButton
                disabled={rowData.status === PRODUCT_STATUS.DRAFT && readOnlyEditUpdate}
                onClick={() => navigate(`${detailUrl}${rowData.id}`)}
              >
                {rowData.status === PRODUCT_STATUS.DRAFT ? GENERAL_CONSTANTS.EDIT : GENERAL_CONSTANTS.VIEW}
              </StyledButton>
            </ReadOnlyWrapper>
          </div>
          <div style={{ width: "fit-content" }}>
            <ReadOnlyWrapper readOnly={readOnlyEditUpdate}>
              {isProductStatusPublished(rowData.status) && (
                <StyledButton
                  disabled={readOnlyEditUpdate}
                  onClick={() => { setSelectedProduct(rowData.id); setShowUpdateModal('update') }}
                >
                  {GENERAL_CONSTANTS.UPDATE}
                </StyledButton>
              )}
            </ReadOnlyWrapper>
            {rowData.status === PRODUCT_STATUS.READY &&
              <ReadOnlyWrapper readOnly={readOnlyEditUpdate}>
                <StyledButton
                  disabled={readOnlyEditUpdate}
                  onClick={() => { setSelectedProduct(rowData.id); setShowUpdateModal('revise') }}
                >
                  {GENERAL_CONSTANTS.REVISE}
                </StyledButton>
              </ReadOnlyWrapper>
            }
          </div>
        </FlexboxContainerComponent>
      }
    };
  }, [readOnlyEditUpdate, setShowUpdateModal, typeConfig]);

  // Memoize image column to avoid recreation
  const imageColumn = useMemo(() => ({
    render: (rowData: any) => {
      return (
        <>
          {rowData.imagePath !== null && <img src={process.env.REACT_APP_MEDIA_URL! + rowData.imagePath} alt={rowData.name} style={{ width: '30px', height: '30px', objectFit: 'contain', margin: '0px 0px -5px 0px' }} />}
        </>
      );
    }
  }), []);

  // Update columns when productColumns or readOnly changes
  useEffect(() => {
    const updatedColumns = productColumns.map((col) => {
      if (col.field === 'actions') {
        return { ...col, ...actionColumn };
      }

      if (col.field === 'imagePath') {
        const image = productColumns.find(x => x.title === 'Image');
        return { ...col, ...imageColumn, hidden: image?.hidden };
      }

      // Apply defaultSort to the currently sorted column
      if (col.field === sortField && sortOrder) {
        return { ...col, defaultSort: sortOrder };
      }

      return { ...col, defaultSort: undefined };
    });
    setColumns(updatedColumns);
  }, [productColumns, actionColumn, imageColumn, sortField, sortOrder]);

  // Build query params
  const buildQueryParams = useCallback(() => {
    let params = `page=${page + 1}&pageSize=${rowsPerPage}`;

    if (filters) {
      if (filters.publishStatus.id) {
        params += `&status=${filters.publishStatus.code?.toLowerCase()}`;
      }
      if (filters.brands && filters.brands.length > 0) {
        params += `&brandIds=${filters.brands.map((brand) => brand.id).join(',')}`;
      }
      if (filters.vendor.id) {
        params += `&vendorId=${filters.vendor.id}`;
      }
      if (filters.country.id) {
        params += `&countryIso=${filters.country.id}`;
      }
    }

    if (selectedVendorId) {
      params += `&vendorId=${selectedVendorId}`;
    }

    if (searchTerm && searchTerm.trim() !== '') {
      params += `&search=${searchTerm}`;
    }

    if (sortField && sortOrder) {
      params += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    }

    if (typeConfig.hasCommissionFilter) {
      params += `&productType=${PRODUCT_TYPE.REGULAR}&hasCommissionUS=true&hasCommissionCA=true`;
    } else if (productType) {
      params += `&productType=${productType}`;
    }

    return params;
  }, [page, rowsPerPage, filters, selectedVendorId, searchTerm, sortField, sortOrder, productType]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = buildQueryParams();
      const result = await getAllProducts(queryParams, token);

      if (isMounted.current) {
        setTablesData({
          data: result.data,
          page: result.meta.currentPage - 1,
          totalCount: result.meta.totalRecords,
        });
        setReload(false);
      }
    } catch (error: any) {
      if (isMounted.current) {
        console.error('API error:', error);
        renderToast('error', error.message || `${GENERAL_CONSTANTS.FAILED_TO_FETCH} ${GENERAL_CONSTANTS.products}`);
      }
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams, token, setLoading, setReload]);

  // Fetch data on dependency changes
  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters, selectedVendorId, searchTerm, sortField, sortOrder, reload]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filters, selectedVendorId, searchTerm]);

  const handleDeletion = useCallback((perform: boolean) => {
    if (perform) {
      setSelectedRows([]);
      fetchData();
    }
    setShowDeleteModal(false);
  }, [setSelectedRows, setShowDeleteModal, fetchData]);

  const handleProductUpdate = useCallback(() => {
    setShowUpdateModal('none');
    setSelectedProduct(undefined);
  }, [setShowUpdateModal]);

  const handleRowsPerPageChange = useCallback((newPageSize: number) => {
    setRowsPerPage(newPageSize);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage - 1);
  }, []);

  const handleSortChange = useCallback((orderBy: number, orderDirection: string) => {
    if (orderBy >= 0 && columns && columns[orderBy] && (orderDirection === 'asc' || orderDirection === 'desc')) {
      const field = String(columns[orderBy].field);
      setSortField(field);
      setSortOrder(orderDirection);
    }
  }, [columns]);

  const totalPages = Math.ceil(tablesData.totalCount / rowsPerPage);

  return (
    <div className="product-listing">
      <MatDataGrid
        columns={columns}
        data={tablesData.data}
        isLoading={loading}
        sorting
        enableSelection={!typeConfig.hideActionButtons}
        onSelectionChange={(rows) => setSelectedRows(rows)}
        onOrderChange={handleSortChange}
        headerStyle={{
          color: '#FFF',
          fontSize: '16px',
          height: 50,
          padding: '0px 5px',
          fontFamily: "'Aeonik', sans-serif"
        }}
        rowStyle={{
          fontSize: '14px',
          wordWrap: 'break-word',
        }}
        customPagination={{
          itemsPerPage: rowsPerPage,
          setItemsPerPage: handleRowsPerPageChange,
          currentPage: page + 1,
          setCurrentPage: handlePageChange,
          totalPages: totalPages > 0 ? totalPages : 1,
          baseRoute: 'products',
          shouldRedirect: false
        }}
      />
      <ProductListingActions
        showDeleteModal={showDeleteModal}
        selectedRows={selectedRows}
        onDelete={handleDeletion}
        showUpdateModal={showUpdateModal}
        onUpdate={handleProductUpdate}
        productId={selectedProduct}
        showPublishModal={showPublishModal}
        setShowPublishModal={setShowPublishModal}
        setSelectedRows={setSelectedRows}
        setReload={setReload}
        productType={productType}
      />
    </div>
  );
};

export default ProductListingTable;
